import React, { useMemo } from 'react';
import { getFunctionNodeEdgeSlotOptions } from '../../constants/edgeInputSlots';
import styles from './index.module.scss';

const PLACEHOLDER_ICON = 'https://photostudio-ai-oss.arcsoft.com.cn/photostudio-opshub/FreeCanvas/node-image.svg';
const PLACEHOLDER_IMAGE_URLS = new Set([
    'https://photostudio-ai-oss.arcsoft.com.cn/photostudio-opshub/FreeCanvas/node-image.svg',
    'https://photostudio-ai-oss.arcsoft.com.cn/photostudio-opshub/FreeCanvas/node-image.svg',
]);
const TEMPLATE_COVER_SCHEMA_PREFIX = 'free-canvas-preview:v1:';
const PREVIEW_WIDTH = 208;
const PREVIEW_HEIGHT = 160;

const parseTemplateCover = (cover = '') => {
    if (!cover || typeof cover !== 'string' || !cover.startsWith(TEMPLATE_COVER_SCHEMA_PREFIX)) {
        return null;
    }

    try {
        const encodedPayload = cover.slice(TEMPLATE_COVER_SCHEMA_PREFIX.length);
        return JSON.parse(decodeURIComponent(encodedPayload));
    } catch (error) {
        return null;
    }
};

const getNodePreviewImage = (node) => {
    const data = node?.data || {};
    const latestOutput = Array.isArray(data?.latestExecution?.outputs)
        ? data.latestExecution.outputs.find((item) => item?.type === 'image' && item?.url)
        : null;
    const candidates = [
        latestOutput?.url,
        data?.src,
        data?.inputParams?.output,
        data?.inputParams?.srcImage,
    ].filter(Boolean);

    return candidates.find((url) => !PLACEHOLDER_IMAGE_URLS.has(url)) || '';
};

const getEdgeLabel = (edge, nodeMap) => {
    const targetNode = nodeMap.get(edge.target);
    const inputSlotKey = edge?.data?.inputSlotKey || '';
    if (!inputSlotKey) return '';

    const option = getFunctionNodeEdgeSlotOptions(targetNode?.data?.functionKey)
        .find((item) => item.key === inputSlotKey);

    return option?.label || inputSlotKey;
};

const buildBezierPath = (sourceNode, targetNode) => {
    const startX = sourceNode.position.x + sourceNode.width;
    const startY = sourceNode.position.y + sourceNode.height / 2;
    const endX = targetNode.position.x;
    const endY = targetNode.position.y + targetNode.height / 2;
    const deltaX = Math.max((endX - startX) * 0.5, 72);
    const c1x = startX + deltaX;
    const c1y = startY;
    const c2x = endX - deltaX;
    const c2y = endY;
    const labelX = (startX + endX) / 2;
    const labelY = (startY + endY) / 2;

    return {
        path: `M ${startX} ${startY} C ${c1x} ${c1y}, ${c2x} ${c2y}, ${endX} ${endY}`,
        labelX,
        labelY,
    };
};

const TemplatePreviewCard = ({ cover = '', templateName = '' }) => {
    const previewSchema = useMemo(() => parseTemplateCover(cover), [cover]);

    const previewData = useMemo(() => {
        if (!previewSchema?.nodes?.length) return null;

        const schemaWidth = Math.max(Number(previewSchema.width) || PREVIEW_WIDTH, 1);
        const schemaHeight = Math.max(Number(previewSchema.height) || PREVIEW_HEIGHT, 1);
        const scale = Math.min(PREVIEW_WIDTH / schemaWidth, PREVIEW_HEIGHT / schemaHeight);
        const nodeMap = new Map((previewSchema.nodes || []).map((node) => [node.id, node]));
        const edges = (previewSchema.edges || [])
            .map((edge) => {
                const sourceNode = nodeMap.get(edge.source);
                const targetNode = nodeMap.get(edge.target);
                if (!sourceNode || !targetNode) return null;
                return {
                    ...edge,
                    ...buildBezierPath(sourceNode, targetNode),
                    label: getEdgeLabel(edge, nodeMap),
                };
            })
            .filter(Boolean);

        return {
            width: schemaWidth,
            height: schemaHeight,
            scale,
            nodes: previewSchema.nodes || [],
            edges,
        };
    }, [previewSchema]);

    if (!previewData) {
        return (
            <div className={styles.previewFrame}>
                {cover ? (
                    <img src={cover} alt={templateName} className={styles.previewImage} />
                ) : (
                    <div className={styles.empty}>暂无预览</div>
                )}
            </div>
        );
    }

    return (
        <div className={styles.previewFrame}>
            <div
                className={styles.previewCanvas}
                style={{
                    width: previewData.width,
                    height: previewData.height,
                    transform: `translate(-50%, -50%) scale(${previewData.scale})`,
                }}
            >
                <svg
                    className={styles.edgeLayer}
                    width={previewData.width}
                    height={previewData.height}
                    viewBox={`0 0 ${previewData.width} ${previewData.height}`}
                >
                    {previewData.edges.map((edge) => (
                        <g key={edge.id}>
                            <path d={edge.path} className={styles.edgePath} />
                            {edge.label ? (
                                <foreignObject
                                    x={edge.labelX - 34}
                                    y={edge.labelY - 12}
                                    width="68"
                                    height="24"
                                >
                                    <div className={styles.edgeLabel}>{edge.label}</div>
                                </foreignObject>
                            ) : null}
                        </g>
                    ))}
                </svg>
                {previewData.nodes.map((node) => {
                    const previewImage = getNodePreviewImage(node);
                    const isImageNode = node.type === 'imageNode';

                    return (
                        <div
                            key={node.id}
                            className={`${styles.node} ${isImageNode ? styles.imageNode : styles.functionNode}`}
                            style={{
                                left: node.position.x,
                                top: node.position.y,
                                width: node.width,
                                height: node.height,
                            }}
                        >
                            <div className={styles.nodeLabel}>{node?.data?.label || node?.data?.imageSequenceLabel || '节点'}</div>
                            <div className={styles.nodeCard}>
                                {previewImage ? (
                                    <img src={previewImage} alt={node?.data?.label || ''} className={styles.nodeImage} />
                                ) : (
                                    <div className={styles.placeholder}>
                                        <img src={PLACEHOLDER_ICON} alt="" width={20} height={20} />
                                        <span>{isImageNode ? '待上传' : '待生成'}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default TemplatePreviewCard;
